/**
 * Subject AI-system identity on the Arcola mirror.
 *
 * The export (and so the Airtable mirror and the cached record) carries five
 * identity columns — `ai_developers`, `ai_models`, `ai_model_names`,
 * `ai_model_versions`, `ai_model_variants` — each a JSON array serialised
 * into the cell, because one incident can name several systems. Mapped to
 * `aiDevelopers`, `aiModels`, `aiModelNames`, `aiModelVersions`,
 * `aiModelVariants` in config/airtable.json.
 *
 * Three empty-ish states are analytically distinct and must never be
 * collapsed (the "AI involved but unattributed" population alone is ~45% of
 * rows):
 *
 *   ["unknown"]   an AI system was evidenced but not identified
 *   []            no AI system was named in the report at all
 *   (absent/'')   identity not derivable for that row — no extraction
 *
 * `ai_developers` and `ai_models` are registry-normalised (spelling/casing
 * variants collapse), so they are safe to group on. But a name not yet in
 * the registry is passed through raw and later collapses to its canonical
 * label, and the whole block is re-derived retroactively — so NO value here
 * is a stable key. Never persist a filter value; key on the incident id.
 */

export const AI_IDENTITY_UNIDENTIFIED = 'Unidentified AI system';
export const AI_IDENTITY_NONE_NAMED = 'No AI system named';
export const AI_IDENTITY_NOT_ASSESSED = 'Not assessed';

/** The three state labels, in the order they should trail a legend. */
export const AI_IDENTITY_STATE_LABELS: readonly string[] = [
  AI_IDENTITY_UNIDENTIFIED,
  AI_IDENTITY_NONE_NAMED,
  AI_IDENTITY_NOT_ASSESSED,
];

export type AiIdentityState = 'named' | 'unidentified' | 'none' | 'not-assessed';

export interface ParsedAiIdentity {
  state: AiIdentityState;
  /** Named values, with any "unknown" sentinel removed. Empty unless `named`. */
  values: string[];
  /** True when an "unknown" sentinel sat alongside named values. */
  hasUnknown: boolean;
}

const UNKNOWN = 'unknown';

/**
 * Parse one identity cell. Anything that is not a well-formed JSON array of
 * strings — absent, blank, malformed, wrong shape — is `not-assessed`, so a
 * broken row degrades to the same bucket as a row with no extraction rather
 * than to a bogus label.
 */
export function parseAiIdentity(raw: unknown): ParsedAiIdentity {
  const notAssessed: ParsedAiIdentity = { state: 'not-assessed', values: [], hasUnknown: false };
  if (Array.isArray(raw)) return classify(raw);
  if (typeof raw !== 'string') return notAssessed;
  const text = raw.trim();
  if (!text.startsWith('[')) return notAssessed;
  try {
    const arr = JSON.parse(text);
    return Array.isArray(arr) ? classify(arr) : notAssessed;
  } catch {
    return notAssessed;
  }
}

function classify(arr: unknown[]): ParsedAiIdentity {
  const rawStrings = arr.filter((v): v is string => typeof v === 'string');
  // A non-empty array with no string members is not a legitimate shape —
  // treat as malformed rather than as "no AI system named".
  if (arr.length > 0 && rawStrings.length === 0) {
    return { state: 'not-assessed', values: [], hasUnknown: false };
  }
  const strings = rawStrings.map((v) => v.trim()).filter(Boolean);
  if (strings.length === 0) return { state: 'none', values: [], hasUnknown: false };
  const hasUnknown = strings.some((v) => v.toLowerCase() === UNKNOWN);
  const values = dedupe(strings.filter((v) => v.toLowerCase() !== UNKNOWN));
  if (values.length === 0) return { state: 'unidentified', values: [], hasUnknown: true };
  return { state: 'named', values, hasUnknown };
}

function dedupe(values: string[]): string[] {
  const out: string[] = [];
  for (const v of values) if (!out.includes(v)) out.push(v);
  return out;
}

/**
 * Chart-dimension labels for one cell: the named values, plus the
 * unidentified marker when an "unknown" sat alongside them; otherwise
 * exactly one state label. Multi-valued — an incident counts once under
 * EACH label, so totals can exceed the incident count.
 */
export function aiIdentityLabels(raw: unknown): string[] {
  const parsed = parseAiIdentity(raw);
  switch (parsed.state) {
    case 'named':
      return parsed.hasUnknown ? [...parsed.values, AI_IDENTITY_UNIDENTIFIED] : parsed.values;
    case 'unidentified':
      return [AI_IDENTITY_UNIDENTIFIED];
    case 'none':
      return [AI_IDENTITY_NONE_NAMED];
    case 'not-assessed':
      return [AI_IDENTITY_NOT_ASSESSED];
  }
}

export function isAiIdentityStateLabel(label: string): boolean {
  return AI_IDENTITY_STATE_LABELS.includes(label);
}

/**
 * Sort labels alphabetically (case-insensitive), with the three state
 * labels trailing in their fixed order so the legend reads named systems
 * first and the empty-ish buckets last.
 */
export function orderAiIdentityLabels(labels: string[]): string[] {
  const named = labels
    .filter((l) => !isAiIdentityStateLabel(l))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  const states = AI_IDENTITY_STATE_LABELS.filter((s) => labels.includes(s));
  return [...named, ...states];
}

/**
 * Whether a record carries an identity cell at all (any of the three
 * states except "absent"). Used to decide if a dataset offers the identity
 * dimensions: the legacy base has none, and there the dimensions would be
 * a single "Not assessed" bar.
 */
export function hasAiIdentityCell(raw: unknown): boolean {
  return typeof raw === 'string' && raw.trim().startsWith('[');
}

/** Short state words for a one-line list cell (the full labels are for legends). */
export const AI_IDENTITY_SHORT_UNIDENTIFIED = 'Unidentified';
export const AI_IDENTITY_SHORT_NONE_NAMED = 'None named';

/**
 * One-line summary of the subject system(s) for a list row, e.g.
 * "ChatGPT 4o Mini (OpenAI)", "Stable Diffusion (Prisma Labs, Stability AI)",
 * "GPT (OpenAI)" when only the family is known, "Unidentified" / "None
 * named" for the two evidenced states, and '' when not assessed.
 *
 * Version and variant are appended only when exactly one name is given and
 * they are single-valued too — the five arrays are not guaranteed to align
 * by index, so with several systems we list names only and leave the
 * breakdown to the detail modal.
 */
export function summarizeAiSystem(cells: {
  developers?: unknown;
  families?: unknown;
  names?: unknown;
  versions?: unknown;
  variants?: unknown;
}): string {
  const developers = parseAiIdentity(cells.developers);
  const families = parseAiIdentity(cells.families);
  const names = parseAiIdentity(cells.names);
  const versions = parseAiIdentity(cells.versions);
  const variants = parseAiIdentity(cells.variants);

  const withDeveloper = (label: string): string =>
    developers.state === 'named' && !developers.values.includes(label)
      ? `${label} (${developers.values.join(', ')})`
      : label;

  if (names.state === 'named') {
    if (names.values.length === 1) {
      const parts = [names.values[0]];
      if (versions.state === 'named' && versions.values.length === 1) parts.push(versions.values[0]);
      if (variants.state === 'named' && variants.values.length === 1) parts.push(variants.values[0]);
      return withDeveloper(parts.join(' '));
    }
    return withDeveloper(names.values.join(', '));
  }
  if (families.state === 'named') return withDeveloper(families.values.join(', '));
  if (developers.state === 'named') return developers.values.join(', ');

  const state = names.state !== 'not-assessed' ? names.state
    : families.state !== 'not-assessed' ? families.state
      : developers.state;
  if (state === 'unidentified') return AI_IDENTITY_SHORT_UNIDENTIFIED;
  if (state === 'none') return AI_IDENTITY_SHORT_NONE_NAMED;
  return '';
}
